<!----
    Author: hjlee
    Description:
        추가 필드 컨트롤
---->
<template>
  <div class="additional-field-control">
    <div class="additional-field-control-title">
      <div v-for="(additionalFieldInfo, index) in infoData.additionalFieldInfos" :key="index" class="additional-field">
        <div v-for="(datum, datumIndex) in additionalFieldInfo.data" :key="datumIndex">
          <div v-if="isVisible(datum, additionalFieldInfo) && (!onlyRequiredFields || datum.essntlFlag === 'Y')">
            <div v-if="getValueType(datum.valueTyCode) === 'title'" class="adit-title">
              {{ $t(additionalFieldInfo.ownerModel.toSnakeCase() + '_adit_field_info.nm.' + datum.id) }}
              <hr>
            </div>
            <display-control
              v-else-if="display"
              :name="'adit_field_info.nm.' + datum.id"
              :options="getOptions(datum)"
              :type="getValueType(datum.valueTyCode)"
              :title="$t(additionalFieldInfo.ownerModel.toSnakeCase() + '_adit_field_info.nm.' + datum.id)"
              :file-info="getFileInfo(datum)"
              :contents="inputVal.additionalFieldData[additionalFieldInfo.ownerModel][datum.id]"
            />
            <input-control
              v-else
              v-model="inputVal.additionalFieldData[additionalFieldInfo.ownerModel][datum.id]"
              :name="'adit_field_info.nm.' + datum.id + '_' + index"
              :required="datum.essntlFlag === 'Y'"
              :options="getOptions(datum)"
              :title="$t(additionalFieldInfo.ownerModel.toSnakeCase() + '_adit_field_info.nm.' + datum.id)"
              :type="getValueType(datum.valueTyCode)"
              :file-info="getFileInfo(datum)"
              :table-name="tableName"
              :readonly="readonly"
              :disabled="disabled"
              :visible-data="value"
              @change="onForceUpdate"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    value: {
      type: Object,
      required: true
    },
    display: {
      type: Boolean,
      required: false
    },
    infoData: {
      type: Object,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    tableName: {
      type: String,
      default: ''
    },
    onlyRequiredFields: {
      type: Boolean,
      default: false
    }
  },
  data: function() {
    return {
      inputVal: undefined
    }
  },
  watch: {
    value: {
      handler(newVal, oldVal) {
        if (newVal) {
          this.inputVal = newVal
        }
      },
      deep: true
    },
    inputVal: {
      handler(newVal, oldVal) {
        this.$emit('input', newVal)
        this.$emit('change', newVal)
        this.$emit('forceUpdate')
      },
      deep: true
    },
    deep: true
  },
  mounted() {
    this.inputVal = this.value
  },
  methods: {
    isVisible(datum, additionalFieldInfo) {
      if (!this.inputVal) {
        return false
      }
      if (additionalFieldInfo.visibleType === 'always') {
        return true
      }
      if (additionalFieldInfo.visibleType === 'none') {
        return false
      }
      let visible = false
      let val = this.inputVal
      const ownerData = additionalFieldInfo.ownerData
      const visibleBys = additionalFieldInfo.visibleBy
      for (let i = 0; i < ownerData.length; i++) {
        if (datum.objectId !== ownerData[i].id) {
          continue
        }
        if (additionalFieldInfo.visibleType === 'simple') {
          let allMatched = true
          for (let j = 0; j < visibleBys.length; j++) {
            val = this.inputVal
            const fieldNames = visibleBys[j].fieldName.split('.')
            val = val[fieldNames[0]]
            if (
              !this.isSimpleMatched(
                val,
                visibleBys[j].fieldName,
                ownerData[i],
                visibleBys[j].fieldNameInOwnerModel
              )
            ) {
              allMatched = false
            }
          }
          if (allMatched) {
            visible = true
            break
          }
        }
      }
      return visible
    },
    isSimpleMatched(value, fieldName, ownerData, fieldNameInOwnerData) {
      if (!value) {
        return false
      }

      if (Array.isArray(value) && value && value.length > 0) {
        const fieldNameInOwnerDataKeys = fieldNameInOwnerData.split('.')
        const fieldNameKeys = fieldName.split('.')
        for (let i = 0; i < value.length; i++) {
          let comp1 = value[i]
          let comp2 = ownerData

          if (
            Object.keys(comp1).length === 3 &&
            comp1.text &&
            comp1.data &&
            comp1.value
          ) {
            comp1 = comp1.data
          }

          // 이미 첫 필드 이름은 사용한 후이므로 1부터 시작
          for (let j = 1; j < fieldNameKeys.length; j++) {
            if (comp1 === undefined || comp1 === null) {
              return false
            }
            comp1 = comp1[fieldNameKeys[j]]
            if (comp1 === undefined || comp1 === null) {
              return false
            }
          }
          for (let j = 0; j < fieldNameInOwnerDataKeys.length; j++) {
            comp2 = comp2[fieldNameInOwnerDataKeys[j]]
            if (comp2 === undefined || comp2 === null) {
              return false
            }
          }

          if (
            comp1 !== undefined &&
            comp2 !== undefined &&
            comp1 !== null &&
            comp2 !== null &&
            comp1 === comp2
          ) {
            return true
          }
        }
        return false
      }
      if (typeof value === 'object') {
        return value[fieldName] === ownerData[fieldNameInOwnerData]
      }
      return value === ownerData[fieldNameInOwnerData]
    },
    getValueType(code) {
      if (code.startsWith('value_type.')) {
        return code.replace('value_type.', '')
      }
      return code
    },
    getFileInfo(datum) {
      if (
        this.getValueType(datum.valueTyCode) === 'singlefile' ||
        this.getValueType(datum.valueTyCode) === 'multifile'
      ) {
        return datum.optn.fileInfo
      }
      return undefined
    },
    getOptions(datum) {
      if (
        !datum ||
        !datum.optn ||
        !datum.optn.options ||
        datum.optn.options.length === 0
      ) {
        return null
      }

      const options = []
      for (let i = 0; i < datum.optn.options.length; i++) {
        const option = datum.optn.options[i]
        if (datum.optn.multilanguage) {
          options.push({
            value: option.value,
            text: this.$t(option.text[0].code)
          })
        } else {
          options.push({
            value: option.value,
            text: option.text
          })
        }
      }

      return options
    }
  }
}
</script>

<style scoped>
.adit-title {
  margin-top: 30px;
  font-weight: bold;
  font-size: 1.3rem;
  color: #555555;
}
</style>
